import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { MessageSquare, X, Send, Loader2, Save, Archive, Copy, Pencil, Check } from 'lucide-react';
import api from '../lib/api';
import { cstDate, cstTime } from '../lib/time';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { streamChat } from '../lib/streamingChat';
import StreamingMarkdown from './StreamingMarkdown';

// Session key is user+date — deterministic, survives logout/new browser
// tab. Server keeps messages for 2 days then auto-purges on next startup,
// so "saves only for the day, then deletes" per user spec.
const SESSION_PREFIX = 'atde_chat_session_v2';

function getTodaySession(username) {
  const today = cstDate(new Date());
  // Deterministic session ID so logging out → logging back in restores chat
  const id = `${username || 'anon'}-${today.replace(/\//g, '')}`;
  const key = `${SESSION_PREFIX}:${today}`;
  return { id, day: today, key };
}

const QUICK = [
  "Daily briefing",
  "Weekly review",
  "What should I do next?",
  "Evaluate my last trade",
  "Am I in NO-TRADE right now?",
];

export default function ChatWidget() {
  const { user, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(() => getTodaySession(user));
  const scrollerRef = useRef(null);
  const cancelRef = useRef(null);
  // Iter 110 — edit-and-restream state. When set, the message at
  // `editIndex` is being edited in-place with the textarea content
  // `editDraft`. On save, we truncate the thread to that index and
  // re-stream a new assistant reply.
  const [editIndex, setEditIndex] = useState(null);
  const [editDraft, setEditDraft] = useState('');

  // Clipboard copy with a toast confirmation. Falls back to the
  // `document.execCommand` path on non-secure contexts.
  const copyToClipboard = useCallback(async (text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Copy failed');
    }
  }, []);

  // Phase 16 §5: hidden by default; respond to QuickTab open events.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('atde:open-chat', onOpen);
    return () => window.removeEventListener('atde:open-chat', onOpen);
  }, []);

  // When the authenticated user changes (login/logout), rebuild the
  // deterministic session. Guarantees chat re-loads after re-auth.
  useEffect(() => {
    setSession(getTodaySession(user));
    setMessages([]);
  }, [user]);

  // Daily rollover check — if the CST day changes while the widget is open,
  // swap to a fresh session. Old conversation remains in the server until
  // operator explicitly archives or it ages out (default 2 days).
  const checkDailyRollover = useCallback(() => {
    const today = cstDate(new Date());
    if (today !== session.day) {
      const fresh = getTodaySession(user);
      setSession(fresh);
      setMessages([]);
    }
  }, [session.day, user]);

  useEffect(() => {
    const iv = setInterval(checkDailyRollover, 60 * 1000);
    return () => clearInterval(iv);
  }, [checkDailyRollover]);

  useEffect(() => {
    if (!open) return;
    api.get(`/chat/history?session_id=${session.id}`)
      .then(({ data }) => setMessages(data.messages || []))
      .catch(() => {});
  }, [open, session.id]);

  // Also reload history when the session changes while panel is closed —
  // ensures a fresh login hydrates yesterday's conversation immediately.
  useEffect(() => {
    if (!user) return;
    api.get(`/chat/history?session_id=${session.id}`)
      .then(({ data }) => setMessages(data.messages || []))
      .catch(() => {});
  }, [session.id, user]);

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: 99999, behavior: 'smooth' });
  }, [messages, busy]);

  const send = async (text, opts = {}) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    // Iter 110 — when editing, we rebuild the local thread so the
    // operator sees their new user message replace the old one and a
    // fresh assistant bubble start streaming beneath it.
    const truncateAt = opts.truncateAt;
    if (truncateAt != null) {
      // Replace the user message at `truncateAt` with the new text and
      // drop everything after it, then append a fresh assistant bubble.
      setMessages((m) => {
        const trimmed = m.slice(0, truncateAt);
        return [
          ...trimmed,
          { role: 'user', text: msg, t: new Date().toISOString() },
          { role: 'assistant', text: '', t: new Date().toISOString(), streaming: true },
        ];
      });
    } else {
      setInput('');
      setMessages((m) => [...m, { role: 'user', text: msg, t: new Date().toISOString() }]);
      setMessages((m) => [...m, {
        role: 'assistant', text: '', t: new Date().toISOString(), streaming: true,
      }]);
    }
    setBusy(true);

    const handle = streamChat({
      kind: 'operator',
      sessionId: session.id,
      message: msg,
      token,
      truncateAt,
      onToken: (chunk) => {
        setMessages((m) => {
          const copy = m.slice();
          const last = copy[copy.length - 1];
          if (last && last.role === 'assistant') {
            copy[copy.length - 1] = { ...last, text: last.text + chunk };
          }
          return copy;
        });
      },
      onDone: () => {
        setMessages((m) => {
          const copy = m.slice();
          const last = copy[copy.length - 1];
          if (last && last.role === 'assistant') {
            copy[copy.length - 1] = { ...last, streaming: false };
          }
          return copy;
        });
        setBusy(false);
      },
      onError: (err) => {
        setMessages((m) => {
          const copy = m.slice();
          const last = copy[copy.length - 1];
          if (last && last.role === 'assistant') {
            copy[copy.length - 1] = {
              ...last,
              streaming: false,
              text: (last.text || '') + `\n\n_// upstream error: ${err}. Retry shortly._`,
            };
          }
          return copy;
        });
        setBusy(false);
      },
    });
    cancelRef.current = handle.cancel;
  };

  const stopStream = () => {
    cancelRef.current?.();
    cancelRef.current = null;
    setBusy(false);
    setMessages((m) => {
      const copy = m.slice();
      const last = copy[copy.length - 1];
      if (last && last.role === 'assistant' && last.streaming) {
        copy[copy.length - 1] = { ...last, streaming: false };
      }
      return copy;
    });
  };

  // Iter 110 — begin editing the user message at `idx`. Pulls the
  // current text into the inline textarea; Save re-streams, Cancel
  // discards the draft.
  const beginEdit = (idx) => {
    if (busy) return;
    const m = messages[idx];
    if (!m || m.role !== 'user') return;
    setEditIndex(idx);
    setEditDraft(m.text || '');
  };
  const cancelEdit = () => {
    setEditIndex(null);
    setEditDraft('');
  };
  const saveEdit = async () => {
    if (editIndex == null) return;
    const text = (editDraft || '').trim();
    if (!text) { toast.error('Message cannot be empty'); return; }
    const idx = editIndex;
    setEditIndex(null);
    setEditDraft('');
    // Pass truncateAt=idx → server trims to the first `idx` messages
    // and re-streams the new user+assistant pair at that slot.
    await send(text, { truncateAt: idx });
  };

  const archive = async () => {
    if (!messages.length) { toast.error('Nothing to archive yet.'); return; }
    // Phase 16 §4: snapshot messages BEFORE the await so we can restore
    // them on failure. Do not clear UI state until both writes succeed.
    const snapshot = messages.slice();
    try {
      // Save chat to LOGS tab (Phase 33 §11 auto-titles via log_title_engine).
      // Phase 41.D fix — pass the LOCAL message snapshot so coach-daily /
      // coach-weekly responses (which don't persist to db.chat_sessions)
      // are still captured in the archived log.
      const firstUser = snapshot.find((m) => m.role === 'user')?.text || 'saved conversation';
      const { data } = await api.post('/chat/archive', {
        session_id: session.id,
        message: firstUser,
        messages: snapshot,
      });
      toast.success(`Saved to Logs · ${data.title || 'session'}`);
      // Phase 51.M+ — also drop an inline system marker into the thread
      // so the operator sees a visible "chat saved · 2:34 pm" line
      // instead of having to check the Logs tab to confirm.
      const now = new Date();
      setMessages((m) => [
        ...m,
        {
          role: 'system',
          text: `// chat saved · ${cstDate(now)} ${cstTime(now)}${data.title ? ` · "${data.title}"` : ''}`,
          t: now.toISOString(),
        },
      ]);
    } catch (e) {
      // §4B: failure path — never delete conversation.
      toast.error('Save failed — conversation kept');
      setMessages(snapshot);
    }
  };

  if (!open) {
    // Phase 16 §5: chat trigger now lives inside QuickTab. Hide the
    // legacy floating FAB.
    return null;
  }

  return ReactDOM.createPortal(
    <div
      data-testid="chat-panel"
      style={{
        position: 'fixed', zIndex: 9999,
        bottom: '1.25rem', right: '1.25rem',
        boxShadow: '0 0 30px rgba(8,247,254,0.2)',
      }}
      className="w-[95vw] max-w-[440px] h-[75vh] max-h-[640px] panel brackets flex flex-col"
    >
      <span className="bk-bl" /><span className="bk-br" />
      <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border-default)]">
        <div>
          <div className="font-display uppercase tracking-[0.3em] text-[12px] text-cyan-sys glow-cyan">
            ATDE-Assist
          </div>
          <div className="text-[9px] tracking-[0.25em] text-[var(--txt-secondary)] uppercase">
            session · {session.day}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            data-testid="chat-archive"
            onClick={archive}
            title="Save this conversation to the Log and start fresh"
            className="text-cyan-sys hover:text-black hover:bg-cyan-sys px-1.5 py-1 border border-cyan-sys"
          >
            <Save size={12} />
          </button>
          <button
            data-testid="chat-close"
            onClick={() => setOpen(false)}
            className="text-[var(--txt-secondary)] hover:text-sell px-1"
            aria-label="close"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="flex-1 overflow-y-auto p-3 space-y-3 bg-[var(--bg-void)] scanlines font-mono text-[12px] leading-[1.6]"
      >
        {messages.length === 0 && (
          <div className="text-[var(--txt-secondary)]">
            <div className="mb-2 text-cyan-sys">// briefing assistant online. ask anything.</div>
            <div className="text-[10px] text-[var(--txt-muted)] mb-3 tracking-wider">
              // conversations auto-reset each CST day. click <Archive size={9} className="inline" /> to save.
            </div>
            <div className="grid grid-cols-1 gap-1 mt-3">
              {QUICK.map((q) => (
                <button
                  key={q}
                  data-testid={`chat-quick-${q.slice(0, 10).replace(/\s/g, '-')}`}
                  onClick={() => send(q)}
                  className="text-left border border-[var(--border-muted)] hover:border-cyan-sys px-2 py-1.5 text-cyan-sys text-[11px]"
                >
                  ▸ {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => {
          const prev = i > 0 ? messages[i - 1] : null;
          const thisDay = m.t ? cstDate(m.t) : null;
          const prevDay = prev?.t ? cstDate(prev.t) : null;
          const showDivider = thisDay && thisDay !== prevDay;
          return (
            <React.Fragment key={i}>
              {showDivider && (
                <div
                  data-testid={`chat-day-${thisDay}`}
                  className="flex items-center gap-2 my-3 text-[9px] font-mono tracking-[0.3em] uppercase text-cyan-sys"
                >
                  <span className="flex-1 border-t border-[var(--border-default)]" />
                  <span className="px-2">// {thisDay}</span>
                  <span className="flex-1 border-t border-[var(--border-default)]" />
                </div>
              )}
              <div data-testid={`chat-msg-${m.role}`} className="whitespace-pre-wrap group">
                {m.role === 'user' ? (
                  <>
                    <div className="text-hold text-[10px] tracking-widest uppercase mb-0.5 flex items-center justify-between">
                      <span>// operator</span>
                      <div className="flex items-center gap-2">
                        {m.t && <span className="text-[var(--txt-muted)] normal-case tracking-normal">{cstTime(m.t)}</span>}
                        {editIndex !== i && !busy && (
                          <button
                            data-testid={`chat-edit-${i}`}
                            onClick={() => beginEdit(i)}
                            className="text-[var(--txt-muted)] hover:text-cyan-sys opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Edit this message and regenerate the reply"
                            aria-label="edit message"
                          >
                            <Pencil size={11} />
                          </button>
                        )}
                      </div>
                    </div>
                    {editIndex === i ? (
                      <div className="flex flex-col gap-1.5">
                        <textarea
                          data-testid={`chat-edit-input-${i}`}
                          value={editDraft}
                          autoFocus
                          onChange={(e) => setEditDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                              e.preventDefault(); saveEdit();
                            }
                          }}
                          rows={Math.min(6, (editDraft.match(/\n/g) || []).length + 1)}
                          className="w-full bg-black border border-cyan-sys text-cyan-sys outline-none px-2 py-1 font-mono text-[12px] resize-y"
                        />
                        <div className="flex items-center gap-2 text-[9px] tracking-widest uppercase">
                          <button
                            data-testid={`chat-edit-save-${i}`}
                            onClick={saveEdit}
                            className="border border-cyan-sys text-cyan-sys hover:bg-cyan-sys hover:text-black px-2 py-1 flex items-center gap-1"
                            title="save (⌘+Enter)"
                          >
                            <Check size={11} /> save &amp; regenerate
                          </button>
                          <button
                            data-testid={`chat-edit-cancel-${i}`}
                            onClick={cancelEdit}
                            className="border border-[var(--border-muted)] text-[var(--txt-secondary)] hover:text-sell hover:border-sell px-2 py-1"
                            title="cancel (esc)"
                          >
                            cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-[var(--txt-primary)]">{m.text}</div>
                    )}
                  </>
                ) : m.role === 'system' ? (
                  <div className="text-cyan-sys text-[10px] tracking-widest uppercase text-center py-1 opacity-80 flex items-center gap-2 justify-center">
                    <Save size={9} />
                    <span className="normal-case tracking-wider">{m.text}</span>
                  </div>
                ) : (
                  <>
                    <div className="text-cyan-sys text-[10px] tracking-widest uppercase mb-0.5 flex items-center justify-between">
                      <span className="flex items-center gap-1.5">// atde-assist
                        {m.streaming && (
                          <span className="text-cyan-sys flex items-center gap-1 normal-case tracking-normal">
                            <Loader2 size={9} className="animate-spin" /> streaming
                          </span>
                        )}
                      </span>
                      <div className="flex items-center gap-2">
                        {m.t && <span className="text-[var(--txt-muted)] normal-case tracking-normal">{cstTime(m.t)}</span>}
                        {!m.streaming && m.text && (
                          <button
                            data-testid={`chat-copy-${i}`}
                            onClick={() => copyToClipboard(m.text)}
                            className="text-[var(--txt-muted)] hover:text-cyan-sys opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Copy reply to clipboard"
                            aria-label="copy reply"
                          >
                            <Copy size={11} />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="text-buy atde-md">
                      <StreamingMarkdown text={m.text} streaming={!!m.streaming} />
                    </div>
                  </>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="border-t border-[var(--border-default)] flex items-end gap-1 p-2 bg-[var(--bg-panel)]"
      >
        <span className="text-cyan-sys text-[12px] px-1 pb-1.5">&gt;</span>
        <textarea
          data-testid="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            // Phase 51.M+ — Enter sends, Shift+Enter inserts a newline.
            // Matches the ChatGPT / Claude / most-messenger convention.
            if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
              e.preventDefault();
              if (!busy && input.trim()) send();
            }
          }}
          disabled={busy}
          placeholder="ask the assistant… (Shift+Enter = newline)"
          rows={1}
          className="flex-1 bg-transparent outline-none font-mono text-[12px] text-cyan-sys placeholder:text-[var(--txt-muted)] resize-none max-h-32 overflow-y-auto py-1"
          autoComplete="off"
        />
        <button
          data-testid="chat-send"
          type="submit"
          disabled={busy || !input.trim()}
          className="ml-auto border border-cyan-sys text-cyan-sys px-3 py-1.5 hover:bg-cyan-sys hover:text-black disabled:opacity-40"
          aria-label="send"
          title="Send (Enter)"
        >
          <Send size={12} />
        </button>
        {busy && (
          <button
            data-testid="chat-stop"
            type="button"
            onClick={stopStream}
            className="border border-sell text-sell px-2 py-1.5 hover:bg-sell hover:text-black"
            aria-label="stop streaming"
            title="Stop"
          >
            <X size={12} />
          </button>
        )}
      </form>
    </div>,
    document.body
  );
}
